import React, { FunctionComponent, type SVGProps } from 'react';
import clsx from 'clsx';
import type { IconName, IconPrefix } from '@awesome.me/kit-e8cfc819bb/icons'
import { faIcons } from '@/shared/fa-library'

import styles from './icon.module.css';

export type IconType = IconName;

type SizeProp = '2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '1x' | '2x' | '3x' | '4x' | '5x' | '6x' | '7x' | '8x' | '9x' | '10x'

export type IconProps = {
  pack?: IconPrefix
  /* Svg name */
  icon: IconType;
  /* Svg size */
  size?: SizeProp,
  variant?: 'brand' | 'ideeri-primary' | 'ideeri-secondary' | 'ideeri-light' | 'ideeri-dark';
  className?: string[] | string
} & Omit<SVGProps<SVGSVGElement>, 'size' | 'className'>

const findIcon = (name: IconName, pack: IconPrefix = 'far') =>
  faIcons.get(`${pack}-${name}`)
    ?? faIcons.get(`far-${name}`)
    ?? faIcons.get(`fab-${name}`)
    ?? faIcons.get(`fakd-${name}`)

/* le nom correspond à une icône de la bibliothèque (ex : suffixe d'un champ, icône ou texte) */
export const isIconName = (name: string): name is IconType => !!findIcon(name as IconName)

/**
 * An Icon is a piece of visual element, but we must ensure its accessibility while using it.
 * It can have 2 purposes:
 *
 * - *decorative only*: for example, it illustrates a label next to it. We must ensure that it is ignored by screen readers, by setting `aria-hidden` attribute (ex: `<Icon icon="check" aria-hidden />`)
 * - *non-decorative*: it means that it delivers information. For example, an icon as only child in a button. The meaning can be obvious visually, but it must have a proper text alternative via `aria-label` for screen readers. (ex: `<Icon icon="print" aria-label="Print this document" />`)
 *
 * Rend directement le SVG de la définition Font Awesome, avec le markup et les classes de
 * FontAwesomeIcon : évite d'embarquer fontawesome-svg-core (~20 KB gzip) dans le JS client.
 */
export const Icon: FunctionComponent<IconProps> = ({
  pack = 'far',
  icon,
  size = 'sm',
  variant,
  className,
  ...props
}: IconProps) => {
  const definition = findIcon(icon, pack)
  if (!definition) return null

  const [width, height, , , pathData] = definition.icon
  const labelled = ['aria-label', 'aria-labelledby', 'title', 'role'].some(attribute => attribute in props)

  return (
    <svg
      data-prefix={definition.prefix}
      data-icon={definition.iconName}
      className={clsx('svg-inline--fa', `fa-${definition.iconName}`, styles.icon, variant && styles[variant], className, `fa-${size}`)}
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden={labelled ? undefined : true}
      {...props}
      >
      {Array.isArray(pathData) ? (
        <g className="fa-duotone-group">
          <path className="fa-secondary" fill="currentColor" d={pathData[0]}/>
          <path className="fa-primary" fill="currentColor" d={pathData[1]}/>
        </g>
      ) : (
        <path fill="currentColor" d={pathData}/>
      )}
    </svg>
  )
};
